import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { getCdnImage } from '../lib/imageUtils';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll to shrink header/logo
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  const serviceSubItems = [
    {
      title: 'Vandværksservice & ledningsarbejde',
      desc: 'Brud, målerbrønde, afpropning & jordraket',
      icon: 'fa-solid fa-faucet-drip',
      href: '/ydelser/vandvaerksservice-ledningsarbejde',
    },
    {
      title: 'Autoriseret kloakarbejde & tilslutning',
      desc: 'Nybyg, pumpestationer, samletanke & rottespærrer',
      icon: 'fa-solid fa-water',
      href: '/ydelser/kloakarbejde-tilslutning',
    },
    {
      title: 'Belægning & anlæg',
      desc: 'Indkørsler, afretning, stabilgrus & fliser',
      icon: 'fa-solid fa-layer-group',
      href: '/ydelser/belaegning-anlaeg',
    },
    {
      title: 'Vinterservice & snerydning',
      desc: 'Snerydning og saltning for erhverv og butikker',
      icon: 'fa-solid fa-snowflake',
      href: '/ydelser/vinterservice-snerydning',
    },
  ];

  const isServiceActive = location.pathname.startsWith('/ydelser') || location.pathname === '/hvad-laver-vi';

  return (
    <header className="sticky top-0 z-40 w-full bg-white shadow-sm border-b border-slate-200">
      {/* Top info bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 sm:gap-6">
            <span className="flex items-center gap-1.5 text-slate-400">
              <i className="fa-solid fa-shield-halved text-emerald-400" aria-hidden="true" />
              <span className="hidden sm:inline">Aut. kloakmester</span>
              <span className="sm:hidden">Aut. kloak</span>
            </span>
            <span className="hidden md:flex items-center gap-1.5 text-slate-400">
              <i className="fa-solid fa-location-dot text-emerald-400" aria-hidden="true" />
              <span>Karlebyvej 32, 4894 Øster Ulslev</span>
            </span>
            <span className="hidden lg:flex items-center gap-1.5 text-slate-400">
              <i className="fa-solid fa-id-card text-emerald-400" aria-hidden="true" />
              <span>CVR: 28498675</span>
            </span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6 ml-auto">
            <a
              href="mailto:martinl4894@gmail.com"
              className="flex items-center gap-1.5 text-slate-300 hover:text-emerald-400 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-emerald-400" aria-hidden="true" />
              <span className="hidden sm:inline">martinl4894@gmail.com</span>
              <span className="sm:hidden">Mail os</span>
            </a>
            <a
              href="tel:40309708"
              className="flex items-center gap-1.5 font-bold text-white hover:text-emerald-400 transition-colors whitespace-nowrap bg-emerald-700/40 px-2.5 py-0.5 rounded border border-emerald-600/50"
            >
              <i className="fa-solid fa-phone text-emerald-400" aria-hidden="true" />
              <span>40 30 97 08</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between transition-all duration-300 ${
            isScrolled ? 'h-16 sm:h-18 md:h-20' : 'h-24 sm:h-28'
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 py-1 flex-shrink-0 group focus:outline-none"
            aria-label="ENTREPRENØR LANG ApS Forside"
          >
            <img
              src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/entreprenrlang.jpg', {
                width: 500,
                quality: 95,
              })}
              alt="ENTREPRENØR LANG ApS - Aut. kloakmester & entreprenør"
              className={`w-auto object-contain transition-all duration-300 group-hover:scale-[1.02] ${
                isScrolled ? 'h-11 sm:h-13 md:h-14' : 'h-16 sm:h-20 md:h-22'
              }`}
            />
          </Link>

          {/* Desktop Navigation: visible ONLY on min-width 1200px per requirements */}
          <nav
            className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2 text-sm font-semibold"
            aria-label="Hovednavigation"
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'text-emerald-800 bg-emerald-50 font-bold border-b-2 border-emerald-700'
                    : 'text-slate-700 hover:text-emerald-800 hover:bg-slate-100'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <NavLink
                to="/ydelser"
                className={() =>
                  `px-3 py-2 rounded-md transition-colors flex items-center gap-1.5 ${
                    isServiceActive
                      ? 'text-emerald-800 bg-emerald-50 font-bold border-b-2 border-emerald-700'
                      : 'text-slate-700 hover:text-emerald-800 hover:bg-slate-100'
                  }`
                }
              >
                <span>Ydelser</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesDropdownOpen ? 'rotate-180 text-emerald-700' : 'text-slate-400'
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {/* Dropdown Menu */}
              {servicesDropdownOpen && (
                <div
                  className="absolute left-0 top-full pt-2 w-80 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  role="menu"
                >
                  <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-2.5 space-y-1">
                      <Link
                        to="/ydelser"
                        className={`block px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider mb-1.5 transition-colors ${
                          location.pathname === '/ydelser' || location.pathname === '/hvad-laver-vi'
                            ? 'bg-emerald-50 text-emerald-800 border-l-4 border-emerald-600 font-extrabold'
                            : 'bg-emerald-50/60 hover:bg-emerald-100/70 text-emerald-800'
                        }`}
                      >
                        Se alle ydelser & oversigt
                      </Link>

                      {serviceSubItems.map((item) => {
                        const isSubActive = location.pathname === item.href;
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={`block p-2.5 rounded-lg transition-all ${
                              isSubActive
                                ? 'bg-emerald-50 text-emerald-900 font-semibold border-l-4 border-emerald-600 shadow-xs'
                                : 'hover:bg-slate-50 text-slate-800'
                            }`}
                            role="menuitem"
                          >
                            <div className={`text-sm font-semibold leading-tight ${isSubActive ? 'text-emerald-800 font-bold' : 'text-slate-900 group-hover:text-emerald-800'}`}>
                              {item.title}
                            </div>
                            <div className={`text-xs mt-0.5 leading-snug ${isSubActive ? 'text-emerald-700/90 font-medium' : 'text-slate-500'}`}>
                              {item.desc}
                            </div>
                          </Link>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'text-emerald-800 bg-emerald-50 font-bold border-b-2 border-emerald-700'
                    : 'text-slate-700 hover:text-emerald-800 hover:bg-slate-100'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'text-emerald-800 bg-emerald-50 font-bold border-b-2 border-emerald-700'
                    : 'text-slate-700 hover:text-emerald-800 hover:bg-slate-100'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt-os"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'text-emerald-800 bg-emerald-50 font-bold border-b-2 border-emerald-700'
                    : 'text-slate-700 hover:text-emerald-800 hover:bg-slate-100'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Right Header CTAs on Desktop (>=1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href="tel:40309708"
              className="flex items-center gap-2 text-slate-800 font-bold text-sm hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center">
                <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              </span>
              <span>40 30 97 08</span>
            </a>

            <Link
              to="/kontakt-os"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm shadow-sm transition-all hover:shadow hover:-translate-y-0.5 active:translate-y-0"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile hamburger button: Visible strictly on screens UNDER 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            <a
              href="tel:40309708"
              className="hidden items-center justify-center w-10 h-10 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200"
              aria-label="Ring til os på 40 30 97 08"
            >
              <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg text-slate-700 hover:text-emerald-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn menu"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (visible under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden fixed inset-x-0 top-[113px] bottom-0 z-50 bg-slate-950/60 backdrop-blur-sm">
          <div className="bg-white max-h-full overflow-y-auto border-b border-slate-200 px-5 py-6 shadow-2xl space-y-4">
            {/* Quick call banner */}
            <div className="bg-slate-900 text-white p-4 rounded-xl flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Hurtig kontakt</p>
                <p className="text-base font-bold text-emerald-400">40 30 97 08</p>
              </div>
              <a
                href="tel:40309708"
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold text-xs flex items-center gap-2 whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring op</span>
              </a>
            </div>

            {/* Links */}
            <div className="space-y-1 divide-y divide-slate-100">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `block py-3 px-2 text-base font-semibold rounded-lg ${
                    isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-800'
                  }`
                }
              >
                Forside
              </NavLink>

              {/* Mobile Services Accordion */}
              <div className="py-2">
                <button
                  type="button"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="w-full flex items-center justify-between py-2 px-2 text-base font-semibold text-slate-800"
                >
                  <span className={isServiceActive ? 'text-emerald-700' : ''}>Ydelser</span>
                  <i
                    className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${
                      mobileServicesOpen ? 'rotate-180 text-emerald-700' : 'text-slate-400'
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {mobileServicesOpen && (
                  <div className="pl-3 pr-1 pt-1 pb-2 space-y-1 bg-slate-50 rounded-lg mt-1 border border-slate-100">
                    <NavLink
                      to="/ydelser"
                      end
                      className={({ isActive }) =>
                        `block py-2 px-3 text-sm font-semibold rounded-md ${
                          isActive ? 'text-emerald-800 bg-emerald-100/70' : 'text-slate-700'
                        }`
                      }
                    >
                      Oversigt over alle ydelser
                    </NavLink>
                    {serviceSubItems.map((sub) => (
                      <NavLink
                        key={sub.href}
                        to={sub.href}
                        className={({ isActive }) =>
                          `block py-2 px-3 text-sm rounded-md transition-colors ${
                            isActive ? 'text-emerald-800 bg-emerald-100/70 font-bold border-l-2 border-emerald-700' : 'text-slate-600 font-medium hover:text-slate-900'
                          }`
                        }
                      >
                        {sub.title}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              <NavLink
                to="/galleri"
                className={({ isActive }) =>
                  `block py-3 px-2 text-base font-semibold rounded-lg ${
                    isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-800'
                  }`
                }
              >
                Galleri
              </NavLink>

              <NavLink
                to="/om-os"
                className={({ isActive }) =>
                  `block py-3 px-2 text-base font-semibold rounded-lg ${
                    isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-800'
                  }`
                }
              >
                Om os
              </NavLink>

              <NavLink
                to="/kontakt-os"
                className={({ isActive }) =>
                  `block py-3 px-2 text-base font-semibold rounded-lg ${
                    isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-800'
                  }`
                }
              >
                Kontakt
              </NavLink>
            </div>

            {/* Mobile CTA */}
            <div className="pt-3">
              <Link
                to="/kontakt-os"
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-center shadow"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
